import {
  useAuth0,
} from '@auth0/auth0-react';
import {
  queryOptions,
  infiniteQueryOptions,
  QueryClient,
} from '@tanstack/react-query';
import {
  useImpersonation,
} from 'core/context/impersonation';
import {
  GetApiTemplateGetSectionTemplateListParams,
} from 'dtos';
import {
  SectionTemplateSortOrder,
} from 'dtos/sectionTemplateSortOrder';
import {
  getApiTemplateGetMasterTemplateList,
  getApiTemplateGetSectionTemplateList,
} from 'endpoints';

export const TEMPLATE_GC_TIME = 10 * 60 * 1000;
type TemplateScope = readonly [string | null, string | null];
type TemplateKind = 'master' | 'section';

export function useTemplateQueryScope(): TemplateScope {
  const { user } = useAuth0();
  const { impersonation } = useImpersonation();
  // QueryClient is already replaced on login/logout. Also isolate impersonated tenants.
  return [user?.sub ?? null, impersonation?.token ?? null];
}

export const templateKeys = {
  lists: (scope: TemplateScope, kind: TemplateKind) => (
    [kind === 'master' ? 'master-templates' : 'templates', scope] as const
  ),
  detail: (scope: TemplateScope, templateName: string, sectionName?: string) => (
    [sectionName == null ? 'master-template' : 'section-template', scope,
      { templateName, sectionName }] as const
  ),
  // VirtualTable's legacy dataKey accepts only a string; its requests use the keys above.
  settings: (scope: TemplateScope, kind: TemplateKind) => (
    `${kind}-templates-data:${JSON.stringify(scope)}`
  ),
};

export function templateListOptions(
  scope: TemplateScope,
  kind: TemplateKind,
  params: GetApiTemplateGetSectionTemplateListParams = {},
) {
  const page = params.page ?? params.pageIndex ?? 0;
  const request = {
    ...(kind === 'section' ? {
      sectionName: params.sectionName || undefined,
      sortBy: params.sortBy ?? SectionTemplateSortOrder.TemplateName,
    } : {}),
    searchText: params.searchText?.trim() || undefined,
    page,
    pageIndex: page,
    pageSize: params.pageSize ?? 25,
  };
  return queryOptions({
    queryKey: [...templateKeys.lists(scope, kind), 'list', request],
    queryFn: ({ signal }) => (kind === 'master'
      ? getApiTemplateGetMasterTemplateList(request, { signal })
      : getApiTemplateGetSectionTemplateList(request, { signal })),
    gcTime: TEMPLATE_GC_TIME,
    staleTime: TEMPLATE_GC_TIME,
  });
}

export function sectionTemplateOptions(
  scope: TemplateScope,
  sectionName?: string,
  searchText?: string,
) {
  const request = {
    sectionName: sectionName || undefined,
    searchText: searchText?.trim() || undefined,
    sortBy: SectionTemplateSortOrder.SectionThenTemplateName,
    pageSize: 50,
  };
  return infiniteQueryOptions({
    queryKey: [...templateKeys.lists(scope, 'section'), 'infinite', request],
    gcTime: TEMPLATE_GC_TIME,
    staleTime: TEMPLATE_GC_TIME,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => getApiTemplateGetSectionTemplateList({
      ...request,
      page: pageParam,
      pageIndex: pageParam,
    }, { signal }),
    getNextPageParam: (lastPage, _pages, lastPageParam) => (
      lastPage.pageProperties?.hasNextPage ? lastPageParam + 1 : undefined
    ),
  });
}

export async function invalidateTemplate(
  queryClient: QueryClient,
  scope: TemplateScope,
  templateName: string = '',
  sectionName?: string | null,
) {
  const kind = sectionName == null ? 'master' : 'section';
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: templateKeys.lists(scope, kind),
      predicate: ({ queryKey }) => {
        const section = (queryKey[3] as { sectionName?: string } | undefined)?.sectionName;
        return kind === 'master' || !section || section === sectionName;
      },
    }),
    queryClient.invalidateQueries({
      queryKey: templateKeys.detail(scope, templateName, sectionName ?? undefined),
    }),
  ]);
  // Mark canonical requests stale before the settings table asks for them again.
  await queryClient.invalidateQueries({ queryKey: [templateKeys.settings(scope, kind)] });
}
